import { ArrowRight, BookOpen, CalendarDays, Clock, MessageCircle, UserRound } from 'lucide-react';
import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';

import { canonicalSiteUrl } from '@/config';
import { PublicSiteHeader } from '@/components/public-site-header';
import { PublicCrossLinks } from '@/components/public-cross-links';
import { pillarArticles } from '@/content/posts/pillar-articles';

import { whatsappUrl } from '@/lib/whatsapp';

export const Route = createFileRoute('/blog')({
  head: ({ matches }) => {
    if (matches.some((match) => match.routeId === '/blog/$slug')) return {};
    const pageUrl = `${canonicalSiteUrl}/blog`;
    const title = '網頁設計、ERP、WhatsApp Chat Bot、SEO 與網上廣告攻略｜渡橋網誌';
    const description = '渡橋網誌提供香港網頁設計價錢、ERP 系統、WhatsApp Chat Bot 訂製收費、SEO 收費、Google 廣告及 Facebook 廣告的實用攻略和報價比較。';
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: `${canonicalSiteUrl}${pillarArticles[0].image}` },
      ],
      links: [{ rel: 'canonical', href: pageUrl }],
    };
  },
  component: BlogPage,
});

function BlogPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (pathname.replace(/\/$/, '') !== '/blog') return <Outlet />;

  return (
    <div className="min-h-screen bg-[#f4efe4] text-[#102d28]">
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-[#f8f3e9]">
        <PublicSiteHeader locale="zh" active="blog" enHref="/en/blog" zhHref="/blog" />
        <main id="main-content">
          <section className="border-b border-[#143d35]/14 px-5 py-16 sm:px-8 lg:px-24 lg:py-24">
            <div className="max-w-4xl">
              <p className="text-sm font-semibold text-[#815321]">渡橋網誌</p>
              <h1 className="mt-5 text-balance text-5xl font-semibold leading-[1.08] tracking-[-0.04em] sm:text-6xl">
                渡橋網誌｜網頁設計、ERP、WhatsApp Chat Bot、SEO 與網上廣告攻略
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-[#344840]">
                網頁設計、ERP 營運系統、WhatsApp Chat Bot 訂製、SEO、Google Ads 和 Facebook Ads 的收費方式都不同。這裡用香港中小企的角度，拆解價錢、服務範圍、比較方法和常見問題。
              </p>
            </div>
          </section>

          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24 lg:py-16">
            <div className="grid gap-7 md:grid-cols-2">
              {pillarArticles.map((article, index) => (
                <article key={article.slug} className="group border-b border-[#143d35]/20 pb-8">
                  <Link to={`/blog/${article.slug}`} className="block overflow-hidden bg-[#e7dfd2]">
                    <img
                      src={article.image}
                      alt={article.imageAlt}
                      width="1400"
                      height="875"
                      loading={index < 2 ? 'eager' : 'lazy'}
                      className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.015]"
                    />
                  </Link>
                  <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-[#815321]">
                    <span>{article.category}</span><span className="inline-flex items-center gap-1.5 text-[#44574f]"><UserRound className="size-3.5" />{article.author}</span><span className="inline-flex items-center gap-1.5 text-[#44574f]"><CalendarDays className="size-3.5" />{formatArticleDate(article.publishedAt)}</span><span className="inline-flex items-center gap-1.5 text-[#44574f]"><Clock className="size-3.5" />{article.readingTime}</span>
                  </div>
                  <h2 className="mt-3 text-balance text-2xl font-semibold leading-tight tracking-[-0.04em] sm:text-3xl">
                    <Link to={`/blog/${article.slug}`} className="transition hover:text-[#815321]">{article.title}</Link>
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-[#344840]">{article.description}</p>
                  <Link to={`/blog/${article.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">閱讀完整攻略<ArrowRight className="size-4" /></Link>
                </article>
              ))}
            </div>
          </section>

          <section className="grid gap-8 bg-[#063d36] px-5 py-12 text-[#fbf5ea] sm:px-8 lg:grid-cols-[1fr_auto] lg:items-center lg:px-24 lg:py-16">
            <div><BookOpen className="size-8 text-[#d9a267]" /><h2 className="mt-5 text-3xl font-semibold tracking-[-0.04em]">你的情況未必等同價目表</h2><p className="mt-4 max-w-2xl text-sm leading-7 text-[#e6efe8]">傳現有網站、服務、營運流程和預算方向過來，我們可以先幫你分清應該做網站、ERP、SEO，還是先測試廣告。</p></div>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-12 items-center justify-center gap-2 bg-[#d9a267] px-6 text-sm font-semibold text-[#123f37] hover:bg-[#e6b579]">WhatsApp 查詢<MessageCircle className="size-4" /></a>
          </section>
          <PublicCrossLinks current="blog" />
        </main>
        <Footer />
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#143d35]/14 px-5 py-6 sm:px-8 lg:px-24">
      <div className="flex flex-col gap-3 text-center text-sm text-[#344840] md:flex-row md:items-center md:justify-between md:text-left">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link to="/" className="font-semibold text-[#102d28]">渡橋創意有限公司</Link>
          <Link to="/terms" className="hover:text-[#815321]">服務條款</Link>
          <Link to="/privacy" className="hover:text-[#815321]">隱私政策</Link>
        </div>
        <p>© 2026 渡橋創意有限公司。保留所有權利。</p>
      </div>
    </footer>
  );
}

function formatArticleDate(date: string) {
  return new Intl.DateTimeFormat('zh-HK', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(`${date}T00:00:00Z`));
}
